// Centralised GST calculation logic.
// Used by Create PI, Create Bill, Edit PI modal, Edit Bill modal, Print template.

/** Whether a magazine belongs to the Aabkari family (same GST rules). */
export function isAabkariFamily(magazine) {
  return magazine === 'Aabkari Times' || magazine === 'Aabkari Times ENG';
}

/**
 * Resolve the effective GST RATE (as a decimal: 0, 0.05, 0.18) for a given form state.
 * If gst_rate override is set ('0' | '5' | '18'), use it.
 * If gst_rate is 'auto' or undefined → fall back to product/magazine rules:
 *   - Aabkari Times + Event → 18%
 *   - Aabkari Times + Advertisement / Subscription/Advertisement → 5%
 *   - Else → 0%
 */
export function getEffectiveGstRate(state) {
  const override = state?.gst_rate;
  if (override === '0' || override === 0) return 0;
  if (override === '5' || override === 5) return 0.05;
  if (override === '18' || override === 18) return 0.18;
  // 'auto' or undefined → product rules
  const product = state?.product || '';
  const isAdvertisement = product === 'Advertisement' || product === 'Subscription/Advertisement';
  const isEvent = product === 'Event';
  if (isAabkariFamily(state?.magazine) && isEvent) return 0.18;
  if (isAabkariFamily(state?.magazine) && isAdvertisement) return 0.05;
  return 0;
}

/** Default HSN/SAC code based on product. */
export function getDefaultHsn(product) {
  if (product === 'Event') return '998397';
  if (product === 'Advertisement' || product === 'Subscription/Advertisement') return '998363';
  return '';
}

/** Whether the billing state is UP (intra-state GST). */
function isUP(billingState) {
  const s = String(billingState || '').trim().toUpperCase();
  return s === 'UTTAR PRADESH' || s === 'UP';
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Forward calculation: user typed in Gross or Discount.
 * Returns: { discount_amount, discount_percentage, sgst_amount, cgst_amount, igst_amount, total_amount }
 *   gross - discount = net  →  net * rate = gst  →  total = net + gst
 */
export function calcFromGross(state) {
  const gross = Number(state.gross_amount) || 0;
  let discAmt = Number(state.discount_amount) || 0;
  let discPct = Number(state.discount_percentage) || 0;
  // If percentage given but not amount, derive amount; else if amount given, derive percentage
  if (discPct > 0 && (!discAmt || discAmt === 0)) {
    discAmt = round2(gross * discPct / 100);
  } else if (gross > 0) {
    discPct = round2(discAmt / gross * 100);
  }
  const net = Math.max(0, gross - discAmt);
  const rate = getEffectiveGstRate(state);
  const halfRate = rate / 2;
  let sgst = 0, cgst = 0, igst = 0;
  if (rate > 0 && state.billing_state) {
    if (isUP(state.billing_state)) {
      sgst = round2(net * halfRate);
      cgst = round2(net * halfRate);
    } else {
      igst = round2(net * rate);
    }
  }
  const total = round2(net + sgst + cgst + igst);
  return {
    discount_amount: round2(discAmt),
    discount_percentage: discPct,
    sgst_amount: sgst,
    cgst_amount: cgst,
    igst_amount: igst,
    total_amount: total,
  };
}

/**
 * Reverse calculation: user typed in Total (inclusive of GST).
 * Returns: { gross_amount, discount_amount, sgst_amount, cgst_amount, igst_amount, total_amount }
 *   net = total / (1 + rate)  →  gst = total - net  →  gross = net + discount
 */
export function calcFromTotal(state) {
  const total = Number(state.total_amount) || 0;
  const rate = getEffectiveGstRate(state);
  // Derive net (taxable value) by removing GST from total
  const net = rate > 0 ? round2(total / (1 + rate)) : total;
  const gst = round2(total - net);
  let sgst = 0, cgst = 0, igst = 0;
  if (rate > 0 && state.billing_state) {
    if (isUP(state.billing_state)) {
      // Split GST half-half
      sgst = round2(gst / 2);
      cgst = round2(gst - sgst);
    } else {
      igst = gst;
    }
  }
  // gross - discount = net → gross = net + discount (discount amount preserved)
  const discAmt = Number(state.discount_amount) || 0;
  const gross = round2(net + discAmt);
  const discPct = gross > 0 ? round2(discAmt / gross * 100) : 0;
  return {
    gross_amount: gross,
    discount_amount: round2(discAmt),
    discount_percentage: discPct,
    sgst_amount: sgst,
    cgst_amount: cgst,
    igst_amount: igst,
    total_amount: round2(total),
  };
}

/**
 * Single entry point: recalc based on which field the user touched last.
 *   lastEditedField === 'total'  → reverse calc
 *   anything else                → forward calc
 */
export function recalcAll(state) {
  if (state?.last_edited_field === 'total') {
    return { ...state, ...calcFromTotal(state) };
  }
  return { ...state, ...calcFromGross(state) };
}
